import React, { useEffect, useRef, useState } from 'react'
import { LayoutCP } from 'submodules/nerit-framework-ui/common/components/screen/layout/layout/LayoutCP'
import { HeaderCP } from 'submodules/nerit-framework-ui/common/components/screen/layout/header/HeaderCP'
import { FilterHistoricalChartCP } from 'modules/reports/components/filters/filter-historical-chart/FilterHistoricalChartCP'
import { ChartsRequests } from 'submodules/nerit-aquatec-sdk/services/charts/ChartsRequests'
import { useLocation } from 'react-router'
import { useFormStateManager } from 'submodules/nerit-framework-ui/common/form-state-manager/UseFormStateManager'
import { HistoricalChartFiltersFormModel } from 'modules/reports/components/filters/filter-historical-chart/inner/HistoricalChartFiltersFormModel'
import { FormStateManagerUtils } from 'submodules/nerit-framework-ui/common/form-state-manager/FormStateManagerUtils'
import { NotificationHelper } from 'submodules/nerit-framework-ui/common/components/notification/inner/NotificationHelper'
import { SCREEN_FILTER_URL_PARAM } from 'submodules/nerit-framework-ui/common/screens/inner/ScreenTypes'
import { SystemUtils } from 'submodules/nerit-framework-utils/utils/SystemUtils'
import { TableUtils } from 'submodules/nerit-framework-ui/common/components/table/utils/TableUtils'
import { HistoricChartRequestDTO } from 'submodules/nerit-aquatec-sdk/services/charts/dtos/requests/HistoricChartRequestDTO'
import { GenericChartResponseDTO } from 'submodules/nerit-aquatec-sdk/services/charts/dtos/responses/GenericChartResponseDTO'
import { WelcomeContentWithFilterWrapperCP } from 'app/components/welcome-content-with-filter-wrapper/WelcomeContentWithFilterWrapperCP'
import { HistoricalChartLocationResultCP } from 'modules/reports/components/charts/chart-historical-series/HistoricalChartLocationResultCP'
import { RequestHelper } from 'submodules/nerit-framework-utils/sdk-utils/request-manager/RequestHelper'
import { LocationRequests } from 'submodules/nerit-aquatec-sdk/services/location/LocationRequests'
import { IApiReturn } from 'submodules/nerit-framework-utils/sdk-utils/request-manager/types/IApiReturn'
import { ISearchResult } from 'submodules/nerit-framework-utils/sdk-utils/dtos/interfaces/ISearchResult'
import { LocationNameResponseDTO } from 'submodules/nerit-aquatec-sdk/services/location/dtos/responses/LocationNameResponseDTO'
import { ButtonDownloadAllChartsCP } from 'modules/reports/components/charts/chart-export/ButtonDownloadAllChartsCP'
import { ChartExportItemTP } from 'modules/reports/components/charts/chart-export/ChartImageExportUtils'

interface HistoricChartLocationResult {
    locationCode: number
    locationName: string
    data: GenericChartResponseDTO
    requestDTO: HistoricChartRequestDTO
}

type HistoricChartQueryFilters = HistoricChartRequestDTO & {
    locationCodes?: number[]
}

/**
 */
export function ScreenContentChartHistorical(): JSX.Element {

    const routeLocation = useLocation()

    const [formModel, setFormModel] = useState<HistoricalChartFiltersFormModel>(new HistoricalChartFiltersFormModel())
    const formStateManager = useFormStateManager(formModel)

    const [reload, setReload] = useState<number>()
    const [loadedResults, setLoadedResults] = useState<HistoricChartLocationResult[]>([])
    const [isLoading, setIsLoading] = useState<boolean>(false)

    /* Registro das instancias de grafico (uma por ponto) para exportar todas em .zip. */
    const chartsRef = useRef<Map<number, ChartExportItemTP>>(new Map())

    useEffect(init, [])
    useEffect(() => { filter() }, [reload])

    /**
     */
    function init(): void {

        const queryFiltersStr = new URLSearchParams(routeLocation.search).get(SCREEN_FILTER_URL_PARAM)
        let queryFilters: HistoricChartQueryFilters | undefined
        if (!!queryFiltersStr) {
            queryFilters = JSON.parse(queryFiltersStr)
            setFormModel(new HistoricalChartFiltersFormModel({
                fieldCodes: queryFilters?.fieldCodes,
                locationCodes: queryFilters?.locationCodes
                    ?? (queryFilters?.locationCode != null ? [queryFilters.locationCode] : undefined),
                dateRange: queryFilters?.dateRange,
                fieldRegulationCode: queryFilters?.regulationCode,
            }))
            // Para garantir que ira resetar os campos do filtro
            SystemUtils.sleep(100).then(() => {
                setReload(TableUtils.getReloadNumber())
            })
        }
    }

    /**
     */
    async function filter(): Promise<void> {

        if (!reload || !await FormStateManagerUtils.validateRequiredFields(formStateManager))
            return

        setLoadedResults([])
        chartsRef.current.clear()
        const formValues = formStateManager.getFormValues()!

        setIsLoading(true)
        try {
            const namesResponse = await RequestHelper.runRequest<IApiReturn<ISearchResult<LocationNameResponseDTO>>>(
                LocationRequests.getNameList(),
            )
            const locationNameMap = new Map<number, string>(
                (namesResponse.data.data?.list ?? []).map((location) => [location.code, location.name]),
            )

            const sharedFilters = {
                fieldCodes: formValues.fieldCodes,
                regulationCode: formValues.fieldRegulationCode,
                dateRange: formValues.dateRange,
            }

            const results: HistoricChartLocationResult[] = await Promise.all(
                formValues.locationCodes.map(async (locationCode): Promise<HistoricChartLocationResult> => {
                    const requestDTO: HistoricChartRequestDTO = {
                        ...sharedFilters,
                        locationCode,
                    }
                    const response = await RequestHelper.runRequest<IApiReturn<GenericChartResponseDTO>>(
                        ChartsRequests.getHistoricChart(requestDTO),
                    )
                    return {
                        locationCode,
                        locationName: locationNameMap.get(locationCode) ?? `Ponto ${locationCode}`,
                        data: response.data.data!,
                        requestDTO,
                    }
                }),
            )

            setLoadedResults(results)
        } catch {
            NotificationHelper.error(NotificationHelper.DEFAULT_ERROR_GET_MESSAGE)
            setLoadedResults([])
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <LayoutCP
            overflowVertical={true}
            header={
                <HeaderCP title={'Série Histórica'}>
                    {loadedResults.length > 0 && (
                        <ButtonDownloadAllChartsCP
                            getCharts={() => Array.from(chartsRef.current.values())}
                        />
                    )}
                </HeaderCP>
            }
            content={
                <>
                    <FilterHistoricalChartCP
                        formStateManager={formStateManager}
                        onFilter={() => setReload(TableUtils.getReloadNumber())}
                        loading={isLoading}
                    />

                    <WelcomeContentWithFilterWrapperCP showWelcome={!loadedResults.length}>
                        <>
                            {
                                loadedResults.map((result, index) => (
                                    <HistoricalChartLocationResultCP
                                        key={result.locationCode}
                                        data={result.data}
                                        locationName={result.locationName}
                                        regulationName={formStateManager.getFieldValue('regulationName')}
                                        requestDTO={result.requestDTO}
                                        showTopSpacing={index > 0}
                                        onChartCreated={(chart) => chartsRef.current.set(result.locationCode, {
                                            chart,
                                            fileName: `serie_historica_${result.locationName.replace(/\s+/g, '_')}`,
                                        })}
                                    />
                                ))
                            }
                        </>
                    </WelcomeContentWithFilterWrapperCP>
                </>
            }
        />
    )
}
